import { Box } from '@mantine/core';
import { keyframes } from '../utils/keyframes';
import { forwardRef } from 'react';
import { useReducedMotion } from '../hooks';

export type ShakeDirection = 'horizontal' | 'vertical' | 'rotate';

export interface ShakeProps {
  children: React.ReactNode;
  active?: boolean;
  duration?: number;
  intensity?: 'subtle' | 'medium' | 'strong';
  direction?: ShakeDirection;
  infinite?: boolean;
  count?: number;
}

const intensityValues = {
  subtle: { translate: 2, rotate: 1 },
  medium: { translate: 5, rotate: 3 },
  strong: { translate: 10, rotate: 5 },
};

const Shake = forwardRef<HTMLDivElement, ShakeProps>(
  (
    {
      children,
      active = true,
      duration = 500,
      intensity = 'medium',
      direction = 'horizontal',
      infinite = false,
      count = 1,
    },
    ref
  ) => {
    const reducedMotion = useReducedMotion();

    if (reducedMotion || !active) {
      return <Box ref={ref}>{children}</Box>;
    }

    const values = intensityValues[intensity];
    const iterationCount = infinite ? 'infinite' : count;

    const horizontalShake = keyframes({
      '0%, 100%': { transform: 'translateX(0)' },
      '10%, 30%, 50%, 70%, 90%': { transform: `translateX(-${values.translate}px)` },
      '20%, 40%, 60%, 80%': { transform: `translateX(${values.translate}px)` },
    });

    const verticalShake = keyframes({
      '0%, 100%': { transform: 'translateY(0)' },
      '10%, 30%, 50%, 70%, 90%': { transform: `translateY(-${values.translate}px)` },
      '20%, 40%, 60%, 80%': { transform: `translateY(${values.translate}px)` },
    });

    const rotateShake = keyframes({
      '0%, 100%': { transform: 'rotate(0deg)' },
      '10%, 30%, 50%, 70%, 90%': { transform: `rotate(-${values.rotate}deg)` },
      '20%, 40%, 60%, 80%': { transform: `rotate(${values.rotate}deg)` },
    });

    const animation = direction === 'horizontal'
      ? horizontalShake
      : direction === 'vertical'
        ? verticalShake
        : rotateShake;

    return (
      <Box
        ref={ref}
        style={{
          animation: `${animation} ${duration}ms ease-in-out ${iterationCount}`,
        }}
      >
        {children}
      </Box>
    );
  }
);

Shake.displayName = 'Shake';

export default Shake;
